import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import { useAuth } from '../../hooks/useAuth';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  UsersIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  UserIcon,
  UserPlusIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { SuperAdminService, UserProfile } from '../../services/superAdminService';
import { Role } from '../../types';
import { PageTitle } from '../../components/ui/PageTitle';
import CreateCompanyAdminModal from '../../components/admin/CreateCompanyAdminModal';
import { getRoleLabel, getRoleColor } from '../../utils/roleUtils';

const AllUsersManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | 'all'>('all');
  const [isCreateAdminModalOpen, setIsCreateAdminModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  const superAdminService = React.useMemo(() => new SuperAdminService(), []);

  const loadUsers = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const usersData = await superAdminService.getAllUsers();
      setUsers(usersData);
    } catch (err) {
      logger.error('Failed to load users:', err);
      setError('ユーザーの取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [superAdminService]);

  useEffect(() => {
    if (user && user.role === Role.SUPER_ADMIN) {
      loadUsers();
    }
  }, [loadUsers, user]);

  // Super Admin権限チェック
  if (!user || user.role !== Role.SUPER_ADMIN) {
    return <Navigate to='/' replace />;
  }

  const handleCreateAdminSuccess = async (adminData: { email: string; companyName: string }) => {
    setSuccessMessage(`${adminData.companyName}の管理者（${adminData.email}）を作成しました。`);
    setIsCreateAdminModalOpen(false);

    // ユーザー一覧を再取得
    await loadUsers();

    // 成功メッセージを5秒後に消す
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const filteredUsers = users.filter(userProfile => {
    const matchesSearch =
      userProfile.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      userProfile.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (userProfile.company_name &&
        userProfile.company_name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesRole = roleFilter === 'all' || userProfile.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  const roleOptions: { value: Role | 'all'; label: string }[] = [
    { value: 'all', label: '全ての役割' },
    { value: Role.SUPER_ADMIN, label: 'ラクハイ管理者' },
    { value: Role.ADMIN, label: '陸送会社管理者' },
    { value: Role.DISPATCHER, label: '配車担当者' },
    { value: Role.DRIVER, label: 'ドライバー' },
    { value: Role.READ_ONLY, label: '閲覧者' },
  ];

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600'></div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      {/* ヘッダー */}
      <div className='bg-white dark:bg-gray-800 shadow'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center py-6'>
            <div>
              <PageTitle icon={UsersIcon} title='全ユーザー管理' />
              <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                システム内の全ユーザーの管理
              </p>
            </div>
            <button
              onClick={() => setIsCreateAdminModalOpen(true)}
              className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200'
            >
              <UserPlusIcon className='h-4 w-4 mr-2' />
              会社管理者登録
            </button>
          </div>
        </div>
      </div>

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {error && (
          <div className='mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
            <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
          </div>
        )}

        {successMessage && (
          <div className='mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4'>
            <div className='flex items-center'>
              <CheckCircleIcon className='h-5 w-5 text-green-400 mr-2' />
              <p className='text-sm text-green-800 dark:text-green-200'>{successMessage}</p>
            </div>
          </div>
        )}

        {/* 検索とフィルター */}
        <div className='mb-6 grid grid-cols-1 md:grid-cols-3 gap-4'>
          {/* 検索バー */}
          <div className='md:col-span-2'>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' />
              </div>
              <input
                type='text'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500'
                placeholder='名前、メール、会社名で検索...'
              />
            </div>
          </div>

          {/* 役割フィルター */}
          <div className='relative'>
            <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
              <FunnelIcon className='h-5 w-5 text-gray-400' />
            </div>
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value as Role | 'all')}
              className='block w-full pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500'
            >
              {roleOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ユーザー一覧テーブル */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden'>
          <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <div className='flex items-center'>
              <UsersIcon className='h-6 w-6 text-gray-400 mr-2' />
              <h2 className='text-lg font-medium text-gray-900 dark:text-white'>
                ユーザー一覧 ({filteredUsers.length}人)
              </h2>
            </div>
          </div>

          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-gray-200 dark:divide-gray-700'>
              <thead className='bg-gray-50 dark:bg-gray-700'>
                <tr>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    ユーザー
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    役割
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    所属会社
                  </th>
                  <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    登録日
                  </th>
                  <th className='px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider'>
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className='bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700'>
                {filteredUsers.map(userProfile => (
                  <tr
                    key={userProfile.id}
                    onClick={() => navigate(`/admin/users/${userProfile.id}`)}
                    className='hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200'
                  >
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <div className='flex items-center'>
                        <div className='flex-shrink-0 h-10 w-10'>
                          <div className='h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center'>
                            <UserIcon className='h-6 w-6 text-gray-400' />
                          </div>
                        </div>
                        <div className='ml-4'>
                          <div className='text-sm font-medium text-gray-900 dark:text-white'>
                            {userProfile.name}
                          </div>
                          <div className='text-sm text-gray-500 dark:text-gray-400'>
                            {userProfile.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap'>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(userProfile.role)}`}
                      >
                        {getRoleLabel(userProfile.role)}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                      {userProfile.company_name || '-'}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400'>
                      {new Date(userProfile.created_at).toLocaleDateString('ja-JP')}
                    </td>
                    <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                      <div className='flex justify-end space-x-2'>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            navigate(`/admin/users/${userProfile.id}`);
                          }}
                          className='text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'
                        >
                          <PencilIcon className='h-4 w-4' />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div className='text-center py-12'>
                <UsersIcon className='mx-auto h-12 w-12 text-gray-400 mb-4' />
                <p className='text-gray-500 dark:text-gray-400'>
                  {searchTerm || roleFilter !== 'all'
                    ? '検索条件に一致するユーザーが見つかりません'
                    : '登録されているユーザーがありません'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 統計情報 */}
        <div className='mt-8 grid grid-cols-1 md:grid-cols-4 gap-6'>
          {roleOptions.slice(1).map(role => {
            const count = users.filter(u => u.role === role.value).length;
            return (
              <div key={role.value} className='bg-white dark:bg-gray-800 shadow rounded-lg p-6'>
                <div className='text-sm font-medium text-gray-500 dark:text-gray-400'>
                  {role.label}
                </div>
                <div className='mt-2 text-3xl font-bold text-gray-900 dark:text-white'>{count}</div>
                <div className='text-xs text-gray-500 dark:text-gray-400'>人</div>
              </div>
            );
          })}
        </div>

        {/* モーダル */}
        <CreateCompanyAdminModal
          isOpen={isCreateAdminModalOpen}
          onClose={() => setIsCreateAdminModalOpen(false)}
          onSuccess={handleCreateAdminSuccess}
        />
      </div>
    </div>
  );
};

export default AllUsersManagement;
